'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Thermometer,
  Wind,
  Droplets,
  Eye,
  Compass,
  Sun,
  Cloudy,
  CloudSun,
  CloudRain,
  Loader2,
  CloudOff,
} from 'lucide-react';
import {
  liveWeatherSummary,
  LiveWeatherSummaryOutput,
} from '@/ai/flows/live-weather-summary';

function getWeatherIcon(summary: string, isRaining: boolean, className: string = 'w-8 h-8'): React.ReactNode {
  const lowerCaseSummary = summary.toLowerCase();

  if (isRaining) {
    return <CloudRain className={`${className} text-blue-500`} />;
  }
  if (
    lowerCaseSummary.includes('cloudy') ||
    lowerCaseSummary.includes('overcast')
  ) {
    if (
      lowerCaseSummary.includes('partly') ||
      lowerCaseSummary.includes('few') ||
      lowerCaseSummary.includes('scattered')
    ) {
      return <CloudSun className={`${className} text-yellow-500`} />;
    }
    return <Cloudy className={`${className} text-gray-500`} />;
  }
  if (lowerCaseSummary.includes('clear') || lowerCaseSummary.includes('sunny')) {
    return <Sun className={`${className} text-yellow-500`} />;
  }

  return <Thermometer className={`${className} text-primary`} />;
}

/**
 * Detailed weather widget for the sidebar
 * Shows current conditions with temperature, wind, humidity, visibility
 */
export function SidebarWeather() {
  const [weather, setWeather] = useState<LiveWeatherSummaryOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function fetchWeather() {
      try {
        setLoading(true);
        setError(false);
        const result = await liveWeatherSummary({});
        setWeather(result);
      } catch (e) {
        console.error('Sidebar weather error:', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchWeather();

    // Refresh weather every 10 minutes
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="p-4 bg-muted/50 rounded-lg">
        {/* Main weather display skeleton */}
        <div className="flex items-center gap-3 mb-3">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <div>
            <div className="flex items-center gap-1 text-2xl font-bold text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>°F</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
            </div>
          </div>
        </div>

        {/* Weather details grid skeleton */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Wind className="w-3.5 h-3.5 text-primary" />
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>mph</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Compass className="w-3.5 h-3.5 text-primary" />
            <Loader2 className="w-3 h-3 animate-spin" />
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Droplets className="w-3.5 h-3.5 text-primary" />
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>%</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Eye className="w-3.5 h-3.5 text-primary" />
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>mi</span>
          </div>
        </div>

        {/* Field conditions hint */}
        <div className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground text-center">
          SRAC Field Conditions
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center justify-center gap-2 text-muted-foreground py-2">
          <CloudOff className="w-5 h-5" />
          <span className="text-sm">Weather unavailable</span>
        </div>
      </div>
    );
  }

  const WeatherIcon = getWeatherIcon(weather.summary, weather.rain);

  return (
    <Link href="/#weather" className="block">
      <div className="p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
        {/* Main weather display */}
        <div className="flex items-center gap-3 mb-3">
          {WeatherIcon}
          <div>
            <div className="text-2xl font-bold">{weather.temperature}°F</div>
            <div className="text-xs text-muted-foreground">{weather.summary}</div>
          </div>
        </div>

        {/* Weather details grid */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Wind className="w-3.5 h-3.5 text-primary" />
            <span>{weather.windSpeed} mph</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Compass className="w-3.5 h-3.5 text-primary" />
            <span>{weather.windDirection}</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Droplets className="w-3.5 h-3.5 text-primary" />
            <span>{weather.humidity}%</span>
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Eye className="w-3.5 h-3.5 text-primary" />
            <span>{weather.visibility} mi</span>
          </div>
        </div>

        {/* Rain indicator */}
        {weather.rain && (
          <div className="mt-2 pt-2 border-t border-border/50">
            <div className="flex items-center gap-1.5 text-xs text-blue-500">
              <CloudRain className="w-3.5 h-3.5" />
              <span>Currently raining</span>
            </div>
          </div>
        )}

        {/* Field conditions hint */}
        <div className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground text-center">
          SRAC Field Conditions
        </div>
      </div>
    </Link>
  );
}
